import Bugsnag from '@bugsnag/js';
import { useMutation, useQueryClient } from 'react-query';
import { useSetAtom } from 'jotai';
import { loginTypeAtom } from 'auth/userStore';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { API_CLIENT_HEADERS } from 'api/OpenApiConfiguration';
import { useUpdateUserMutation } from 'auth/mutations/userHooks';
import { getAuth, signOut } from 'firebase/auth';
import { LoginType } from 'auth/constants';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import { removeLocalBookingData } from 'components/checkout/v2/utils';
import { useAuth } from '@clerk/nextjs';

type SignInWithPasswordBody = {
  email: string;
  password: string;
  remember_me: '0' | '1';
};

export type SignUpWithPasswordBody = {
  user: {
    email: string;
    phone: string;
    password: string;
    birthdate: string;
    password_confirmation: string;
    first_name: string;
    last_name: string;
    user_type: 'captain' | 'owner' | 'renter';
  };
};

export const useLogoutMutation = ({ redirectUrl }: { redirectUrl?: string } = {}) => {
  const queryClient = useQueryClient();
  const { signOut: clerkSignOut } = useAuth();
  return useMutation(() => axios.delete('/domestic/v1/me/logout'), {
    onSettled: async () => {
      // clear all queries and mutations
      queryClient.clear();

      // Sign out of Firebase Auth
      try {
        await signOut(getAuth());
      } catch (e) {
        Bugsnag.notify(e);
      }

      // Remove local booking data
      try {
        removeLocalBookingData(false, {});
      } catch (e) {
        Bugsnag.notify(e);
      }

      // Always attempt Clerk sign-out last so the redirect wins.
      try {
        await clerkSignOut({ redirectUrl });
      } catch (e) {
        Bugsnag.notify(e);
        // Hard fallback – don’t leave the user on a broken page.
        window.location.replace(redirectUrl);
      }
    },
  });
};

export const useSignUpWithPasswordMutation = (invalidate = true) => {
  const queryClient = useQueryClient();
  const setLoginType = useSetAtom(loginTypeAtom);
  const updateUser = useUpdateUserMutation(invalidate);
  const { enqueueSnackbar } = useLazySnackbar();

  return useMutation(
    (body: SignUpWithPasswordBody) =>
      axios.post('/signup', body, {
        headers: {
          ...API_CLIENT_HEADERS,
          accept: 'application/json',
        },
      }),
    {
      async onSuccess(response: AxiosResponse, body) {
        setLoginType(LoginType.EMAIL_LOGIN);

        await updateUser.mutateAsync({
          birthdate: body.user.birthdate,
        });
        await queryClient.invalidateQueries();
      },
      onError: (response: AxiosError<any>, body) => {
        // we want to ignore the phone_number error since that's handled by modal
        if (response?.response?.data?.resource_errors?.base?.[0]?.includes('phone number')) return;
        if (response?.response?.data?.data?.email) {
          enqueueSnackbar(
            `It looks like the email ${body.user.email} is already associated with an existing account.`,
            {
              variant: 'error',
              autoHideDuration: 6000,
            }
          );
        } else {
          enqueueSnackbar(`There was a problem creating your account. Please contact info@boatsetter.com`, {
            variant: 'error',
            autoHideDuration: 6000,
          });
        }
      },
    }
  );
};

export const useSignInWithPasswordMutation = () => {
  const queryClient = useQueryClient();
  const setLoginType = useSetAtom(loginTypeAtom);
  const { enqueueSnackbar } = useLazySnackbar();

  return useMutation(
    async (body: SignInWithPasswordBody) => {
      try {
        return await axios.post(
          '/signin',
          {
            grant_type: 'password',
            user: body,
          },
          {
            headers: { ...API_CLIENT_HEADERS, accept: 'application/json' },
          }
        );
      } catch (e) {
        Bugsnag.notify(e);
        // on local /signin sometimes redirects to prod/stage with CORS and empty response but actually logs in the user
        // we want to pass true
        if (e.response.data === undefined) {
          return true;
        }
        return Promise.reject(e);
      }
    },
    {
      onError() {
        enqueueSnackbar('Invalid email and password combination. Please try again.', {
          variant: 'error',
          autoHideDuration: 5000,
        });
      },
      async onSuccess() {
        setLoginType(LoginType.EMAIL_LOGIN);
        await queryClient.invalidateQueries();
      },
    }
  );
};
